import type { MarkerFamily, Specialty } from '@sentinel/shared';

/** The shapes of the families of marker V4 (DS §09), on a 64 grid: the shape alone says what a
 *  marker is (rule 2), even without colour and before the icon is read. */
function polygon(
  sides: number,
  radius: number,
  rotation: number,
  centre = { x: 32, y: 32 },
): string {
  return Array.from({ length: sides }, (_, i) => {
    const a = ((rotation + (i * 360) / sides) * Math.PI) / 180;
    const x = centre.x + radius * Math.cos(a);
    const y = centre.y + radius * Math.sin(a);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
}

const HEXAGON = polygon(6, 28, -90);
const RHOMBUS = polygon(4, 30, -90);
const TRIANGLE = polygon(3, 32, -90, { x: 32, y: 38 });
const PENTAGON = polygon(5, 28, 90, { x: 32, y: 30 });
const OCTAGON = polygon(8, 27, 22.5);
const HULL = 'M5 13H59L50.5 52C49.8 55 47.6 57 44.6 57H19.4C16.4 57 14.2 55 13.5 52Z';
const TICKS = 'M32 0v9M32 55v9M0 32h9M55 32h9';

export interface ShapeStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string | undefined;
  strokeLinejoin: 'round';
}

/** Where the icon sits inside the shape: centre and size on the 64 grid. */
export function glyphBox(family: MarkerFamily, specialty: Specialty | null) {
  if (family === 'network') return specialty === 'sensor' ? [32, 29, 30] : [32, 42, 29];
  const boxes: Record<Exclude<MarkerFamily, 'network'>, readonly number[]> = {
    person: [32, 31, 36],
    unit: [32, 32, 34],
    ground: [32, 32, 40],
    air: [32, 32, 32],
    water: [32, 31, 32],
    structure: [32, 32, 28],
    target: [32, 32, 38],
  };
  return boxes[family];
}

export function FamilyShape({
  family,
  specialty,
  shape,
  ticks,
}: {
  family: MarkerFamily;
  specialty: Specialty | null;
  shape: ShapeStyle;
  /** Colour of the four ticks of a structure (the mark of Sentinel). */
  ticks: string;
}) {
  switch (family) {
    case 'person':
      return <circle cx="32" cy="32" r="26" {...shape} />;
    case 'unit':
      return <polygon points={HEXAGON} {...shape} />;
    case 'ground':
      return <rect x="7" y="7" width="50" height="50" rx="5" {...shape} />;
    case 'air':
      return <polygon points={RHOMBUS} {...shape} />;
    case 'water':
      return <path d={HULL} {...shape} />;
    case 'structure':
      return (
        <>
          <rect x="9" y="9" width="46" height="46" rx="12" {...shape} />
          <path d={TICKS} stroke={ticks} strokeWidth="3.6" strokeLinecap="round" />
        </>
      );
    case 'network':
      return <polygon points={specialty === 'sensor' ? PENTAGON : TRIANGLE} {...shape} />;
    case 'target':
      return <polygon points={OCTAGON} {...shape} />;
  }
}
